import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../components/ui/BackButton';
import ActionBar from '../../components/ui/ActionBar';
import Loader from '../../components/ui/Loader';
import OverflowText from '../../components/ui/OverflowTooltip';
import { AppLoadContext } from '../../components/ui/AppLoadContext';
import { getMembersByCohortUpdateId, resetCohortUpdateMembers } from '../../ducks/cohort';
import DataGrid from '../../components/DataGrid';
import withAuth from '../../components/hoc/withAuth';
import { formatPhoneNumber, toDate } from '../../utils/utils';
import { COHORT_UPDATE_RECORD_STATUS, COHORT_DELTA_MEMBER_STATUS_VALUES } from '../../constants';
import styles from './MemberDeltaList.module.scss';

const CellBg = ({ status, children }) => {
    let deltaClass = '';

    switch (status) {
        case COHORT_UPDATE_RECORD_STATUS.Add:
            deltaClass = 'deltaGreen';
            break;
        case COHORT_UPDATE_RECORD_STATUS.UnEnrolled:
            deltaClass = 'deltaRed';
            break;
        case COHORT_UPDATE_RECORD_STATUS.Exception:
            deltaClass = 'deltaYellow';
            break;
        default:
            break;
    }

    return (
        <div className={styles[deltaClass]}>
            {children}
        </div>
    )
}

const MemberDeltaList = () => {
    const { cohortUpdateId } = useParams();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isAppLoaded = useContext(AppLoadContext);
    const members = useSelector((state) => state.cohort.membersUpdate);

    const [selectedStatus, setSelectedStatus] = useState(null);

   const statusCounts = useMemo(() => {
       const counts = {
           [COHORT_UPDATE_RECORD_STATUS.Add]: 0,
           [COHORT_UPDATE_RECORD_STATUS.UnEnrolled]: 0,
           [COHORT_UPDATE_RECORD_STATUS.Exception]: 0,
       };

       members?.forEach(({ recordStatus }) => {
           if (counts.hasOwnProperty(recordStatus)) {
               counts[recordStatus]++;
           }
       });

       return counts;
   }, [members]);

    const filteredMembers = useMemo(() => {
        if (!selectedStatus) return members;
            return members?.filter(mem => mem.recordStatus === selectedStatus);
    }, [members, selectedStatus]);

    const handleStatusClick = (status) => {
        setSelectedStatus(prev => (prev === status ? null : status));
    };

    const columns = [
        {
            id: 'firstName',
            header: 'Name',
            cell: ({ row, cell }) => {
                let name = `${row.original.firstName} ${row.original.lastName}`;
                return (<CellBg status={row.original.recordStatus}><OverflowText id={cell.id}>{name}</OverflowText></CellBg>)
            },
            maxSize: 100,
        },
        {
            id: 'addressLine1',
            header: 'Address',
            cell: ({ row, cell }) => {
                let address = `${row.original.addressLine1} ${row.original.addressLine2 ?? ' '} ${row.original.state} ${row.original.postalCode}`;
                return (<CellBg status={row.original.recordStatus}><OverflowText id={cell.id}>{address}</OverflowText></CellBg>)
            },
            maxSize: 200,
        },
        {
            id: 'birthday',
            header: 'Birthday',
            accessorKey: 'birthday',
            accessorFn: row => (new Date(row.birthday)),
            cell: ({ row }) => <CellBg status={row.original.recordStatus}>{toDate(row.original.birthday)}</CellBg>,
            minSize: 100,
        },
        {
            id: 'email',
            header: 'Email',
            accessorKey: 'email',
            cell: ({ row, cell }) => (<CellBg status={row.original.recordStatus}><OverflowText id={cell.id}>{row.original.email}</OverflowText></CellBg>),
            maxSize: 200,
        },
        {
            id: 'phone',
            header: 'Phone',
            accessorKey: 'phone',
            cell: ({ row }) => (<CellBg status={row.original.recordStatus}><div className="text-nowrap">{formatPhoneNumber(row.original.phone)}</div></CellBg>),
            size: 120,
        },
        {
            id: 'enrollDate',
            header: 'Enroll Date',
            accessorKey: 'enrollDate',
            accessorFn: row => (new Date(row.enrollDate)),
            cell: ({ row }) => (<CellBg status={row.original.recordStatus}>{toDate(row.original.enrollDate)}</CellBg>),
            minSize: 100,
        },
        {
            id: 'terminationDate',
            header: 'Termination Date',
            accessorKey: 'terminationDate',
            accessorFn: row => (new Date(row.terminationDate)),
            cell: ({ row }) => (<CellBg status={row.original.recordStatus}>{toDate(row.original.terminationDate)}</CellBg>),
            minSize: 100,
        },
        {
            id: 'contract',
            header: 'Contract',
            accessorKey: 'contract',
            cell: ({ row }) => (<CellBg status={row.original.recordStatus}>{row.original.contract}</CellBg>),
        },
        {
            id: 'pbpId',
            header: 'PBP ID',
            accessorKey: 'pbpId',
            cell: ({ row }) => (<CellBg status={row.original.recordStatus}>{row.original.pbpId}</CellBg>),
        },
        {
            id: 'salesChannelName',
            header: 'Sales Channel Name',
            accessorKey: 'salesChannelName',
            cell: ({ row }) => (<CellBg status={row.original.recordStatus}>{row.original.salesChannelName}</CellBg>),
        },
        {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            accessorFn: row => (COHORT_DELTA_MEMBER_STATUS_VALUES[row.status.trim()]),
            cell: ({ row }) => (<CellBg status={row.original.recordStatus}>{COHORT_DELTA_MEMBER_STATUS_VALUES[row.original.status.trim()]}</CellBg>),
        },
    ];

    useEffect(() => {
        if (isAppLoaded) {
            dispatch(getMembersByCohortUpdateId(cohortUpdateId));
        }
    }, [dispatch, isAppLoaded, cohortUpdateId]);

    return (
        <>
            <ActionBar back={true}>
                <BackButton
                    onBack={() => {
                        navigate('/admin/cohort-management');
                        dispatch(resetCohortUpdateMembers());
                    }}
                />
                <div className="d-flex gap-3">
                    <span onClick={() => handleStatusClick(COHORT_UPDATE_RECORD_STATUS.Add)} className={`${styles.deltaLegend} ${styles.green} ${selectedStatus === COHORT_UPDATE_RECORD_STATUS.Add ? 'fw-bold' : ''} cursor-pointer px-1`}>New Members ({statusCounts[COHORT_UPDATE_RECORD_STATUS.Add]})</span>
                    <span onClick={() => handleStatusClick(COHORT_UPDATE_RECORD_STATUS.UnEnrolled)} className={`${styles.deltaLegend} ${styles.red} ${selectedStatus === COHORT_UPDATE_RECORD_STATUS.UnEnrolled ? 'fw-bold' : ''} cursor-pointer px-1`}>Unenrolled Members ({statusCounts[COHORT_UPDATE_RECORD_STATUS.UnEnrolled]})</span>
                    <span onClick={() => handleStatusClick(COHORT_UPDATE_RECORD_STATUS.Exception)} className={`${styles.deltaLegend} ${styles.yellow} ${selectedStatus === COHORT_UPDATE_RECORD_STATUS.Exception ? 'fw-bold' : ''} cursor-pointer px-1`}>Exceptions ({statusCounts[COHORT_UPDATE_RECORD_STATUS.Exception]})</span>
                </div>
            </ActionBar>

            <div className="flex-grow-1">
                {members === null ? <Loader /> :
                    <DataGrid
                        columns={columns}
                        data={filteredMembers}
                        enableFilters={false}
                    />}
            </div>
        </>
    );
}

export default withAuth(MemberDeltaList);
